import { z } from 'zod';
import { IssuedStorageObjectKeySchema } from '../storage.js';

/**
 * @aiPurpose per-Lead file capacity caps (#1084).
 *   `LEAD_FILE_MAX_BYTES` mirrors the backend storage-scope ceiling
 *   so the wire layer rejects oversize uploads before issuing a
 *   presigned URL; `LEAD_FILE_MAX_PER_LEAD` is the per-Lead aggregate
 *   live-row cap enforced at file-bind time.
 */
export const LEAD_FILE_MAX_BYTES = 25 * 1024 * 1024;
export const LEAD_FILE_MAX_PER_LEAD = 20;
export const LEAD_FILE_NAME_MAX = 256;

/**
 * @aiPurpose allowlist of MIME content types accepted for a Lead
 *   file. The catalog covers PDFs, common image formats, Office
 *   documents (DOCX/XLSX/XLS/DOC), and plain text — the families
 *   product flagged for v1.
 * @aiSecurity load-bearing — the backend mirrors this list in
 *   `StorageService.validateSize` when `scope === 'lead-file'`. The
 *   presigned upload URL also binds Content-Type so a forged upload
 *   is rejected at PUT time as defense in depth.
 */
export const LEAD_FILE_ALLOWED_MIME = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/msword',
  'application/vnd.ms-excel',
  'text/plain',
] as const;
export type LeadFileMime = (typeof LEAD_FILE_ALLOWED_MIME)[number];
export const LeadFileMimeSchema = z.enum(LEAD_FILE_ALLOWED_MIME);

const LeadFileNameSchema = z
  .string()
  .min(1)
  .max(LEAD_FILE_NAME_MAX)
  .refine((s) => s.trim().length > 0, 'fileName must not be whitespace-only')
  // Block path-traversal payloads at the wire layer. The persistence
  // column stores display text only; segments like `..` or `/` should
  // never appear here.
  .refine(
    (s) => !s.includes('/') && !s.includes('\\') && !s.includes('..'),
    'fileName must not contain path separators',
  );

/**
 * @aiPurpose uploader reference attached to a file row at read time.
 *   Same shape as the comment author ref; deliberately kept distinct
 *   so a future divergence (e.g. virus-scan reviewer) is a localized
 *   change.
 */
export const LeadFileUploaderRefSchema = z
  .object({
    id: z.string().uuid(),
    name: z.string().min(1),
  })
  .strict();
export type LeadFileUploaderRef = z.infer<typeof LeadFileUploaderRefSchema>;

/**
 * @aiPurpose wire projection of a Lead file row.
 * @aiInvariant only live (non-soft-deleted) rows are returned by
 *   `GET /leads/:id/files`. The activity feed surfaces deleted files
 *   via `LeadActivityReferences.file = null` rather than this schema.
 * @aiPii `fileName` is user-supplied display text; `objectKey` is the
 *   issued storage key the caller exchanges for a presigned download
 *   URL through the shared Storage module.
 */
export const LeadFileSchema = z
  .object({
    id: z.string().uuid(),
    leadId: z.string().uuid(),
    uploadedBy: LeadFileUploaderRefSchema.nullable(),
    objectKey: IssuedStorageObjectKeySchema,
    fileName: z.string().min(1).max(LEAD_FILE_NAME_MAX),
    contentType: LeadFileMimeSchema,
    contentLength: z.number().int().positive(),
    createdAt: z.string().datetime(),
  })
  .strict();
export type LeadFile = z.infer<typeof LeadFileSchema>;

/**
 * @aiPurpose request envelope for POST `/leads/:id/files`. The client
 *   has ALREADY exchanged a presigned upload URL via
 *   `/storage/upload-url` with `scope = 'lead-file'` and PUT the bytes;
 *   this endpoint records the metadata row that binds the issued key
 *   to the Lead.
 * @aiSecurity the service re-validates that:
 *     1. `objectKey` was issued to the caller (the embedded userId
 *        segment equals the JWT subject);
 *     2. `contentType` is in `LEAD_FILE_ALLOWED_MIME`;
 *     3. `contentLength` ≤ `LEAD_FILE_MAX_BYTES`;
 *     4. the live-row count for the Lead is below
 *        `LEAD_FILE_MAX_PER_LEAD`;
 *     5. the `objectKey` is not already bound to another Lead.
 */
export const CreateLeadFileRequestSchema = z
  .object({
    objectKey: IssuedStorageObjectKeySchema,
    fileName: LeadFileNameSchema,
    contentType: LeadFileMimeSchema,
    contentLength: z.number().int().positive().max(LEAD_FILE_MAX_BYTES),
  })
  .strict();
export type CreateLeadFileRequest = z.infer<typeof CreateLeadFileRequestSchema>;

export const LeadFileResponseSchema = z.object({ file: LeadFileSchema }).strict();
export type LeadFileResponse = z.infer<typeof LeadFileResponseSchema>;

export const LeadFilesListResponseSchema = z
  .object({
    items: z.array(LeadFileSchema),
    usage: z
      .object({
        count: z.number().int().nonnegative(),
        maxPerLead: z.number().int().positive(),
        maxBytesPerFile: z.number().int().positive(),
      })
      .strict(),
  })
  .strict();
export type LeadFilesListResponse = z.infer<typeof LeadFilesListResponseSchema>;
